/**
 * 사용자 관련 Read 작업 목차
 * 
 * - getUserInfo() - 사용자 정보 조회
 * - getUserLikes() - 사용자의 찜 목록 조회 (liveLikes, artistLikes, commissionLikes, videoLikes)
 * - getUserProfileImage() - 사용자 프로필 이미지 URL 조회
 * - checkNicknameDuplicate() - 닉네임 중복 체크
 * - getUsersByArtistLike() - 특정 아티스트를 찜한 사용자 목록 조회
 * - getUsersByLiveLike() - 특정 라이브를 찜한 사용자 목록 조회
 * - getAllUsers() - 전체 사용자 목록 조회 (관리자용)
 * - getUserLevelInfo() - 사용자 레벨 정보 조회
 * - getTrafficStats() - 유입 경로 통계 조회 (관리자용)
 */

import apiClient from "@/utils/api";

/**
 * 사용자 정보 조회
 * @param {string} userEmail - 사용자 이메일
 * @returns {Promise<Object|null>} 사용자 정보 또는 null
 */
export async function getUserInfo(userEmail) {
  try {
    const response = await apiClient.get(`/api/user/${encodeURIComponent(userEmail)}`);
    return response.data.data || null;
  } catch (error) {
    if (error.response?.status === 404) {
      return null;
    }
    console.error("사용자 정보 조회 오류:", error);
    throw error;
  }
}

/**
 * 사용자의 찜 목록 조회
 * @param {string} userEmail - 사용자 이메일
 * @returns {Promise<Object>} { liveLikes: [], artistLikes: [], commissionLikes: [], videoLikes: [] }
 */
export async function getUserLikes(userEmail) {
  try {
    const response = await apiClient.get(`/api/user/${encodeURIComponent(userEmail)}/likes`);
    return response.data.data || {
      liveLikes: [],
      artistLikes: [],
      commissionLikes: [],
      videoLikes: [],
    };
  } catch (error) {
    console.error("사용자 찜 목록 조회 오류:", error);
    // 오류 시 빈 배열 반환
    return {
      liveLikes: [],
      artistLikes: [],
      commissionLikes: [],
      videoLikes: [],
    };
  }
}

/**
 * 사용자 프로필 이미지 파일명 조회
 * @param {string} userEmail - 사용자 이메일
 * @returns {Promise<string|null>} 프로필 이미지 파일명 또는 null
 */
export async function getUserProfileImageFileName(userEmail) {
  try {
    const userData = await getUserInfo(userEmail);
    return userData?.img || null;
  } catch (error) {
    console.error("사용자 프로필 이미지 조회 오류:", error);
    return null;
  }
}

/**
 * 닉네임 중복 체크
 * @param {string} nickname - 확인할 닉네임
 * @param {string} currentUserEmail - 현재 사용자 이메일 (본인 제외용, optional)
 * @returns {Promise<boolean>} true: 사용 가능, false: 이미 사용 중
 */
export async function checkNicknameDuplicate(nickname, currentUserEmail = null) {
  try {
    const response = await apiClient.get(`/api/user/check-nickname/${encodeURIComponent(nickname)}`, {
      params: { currentEmail: currentUserEmail },
    });
    return response.data.data.available || false;
  } catch (error) {
    console.error("닉네임 중복 체크 오류:", error);
    throw error;
  }
}

/**
 * 특정 아티스트를 찜한 사용자 목록 조회
 * @param {string} artistName - 아티스트 이름
 * @returns {Promise<Array>} 사용자 목록
 */
export async function getUsersByArtistLike(artistName) {
  try {
    const response = await apiClient.get(`/api/user/by-artist-like/${encodeURIComponent(artistName)}`);
    return response.data.data || [];
  } catch (error) {
    console.error("아티스트 찜 사용자 목록 조회 오류:", error);
    return [];
  }
}

/**
 * 특정 아티스트를 찜한 사용자 수 조회
 * @param {string} artistName - 아티스트 이름
 * @returns {Promise<number>} 찜한 사용자 수
 */
export async function getArtistLikeCount(artistName) {
  try {
    const response = await apiClient.get(`/api/user/by-artist-like/${encodeURIComponent(artistName)}/count`);
    return response.data.data.count || 0;
  } catch (error) {
    console.error("아티스트 찜 수 조회 오류:", error);
    return 0;
  }
}

/**
 * 특정 라이브를 찜한 사용자 목록 조회
 * @param {string} liveId - 라이브 ID
 * @returns {Promise<Array>} 사용자 목록
 */
export async function getUsersByLiveLike(liveId) {
  try {
    const response = await apiClient.get(`/api/user/by-live-like/${liveId}`);
    return response.data.data || [];
  } catch (error) {
    console.error("라이브 찜 사용자 목록 조회 오류:", error);
    return [];
  }
}

/**
 * 전체 사용자 목록 조회 (관리자용)
 * @param {string} orderBy - 정렬 기준 ('lastLogin', 'nickname', 'levelPoint' 등, optional)
 * @param {string} order - 정렬 순서 ('asc', 'desc', optional, default: 'desc')
 * @returns {Promise<Array>} 사용자 목록
 */
export async function getAllUsers(orderBy = null, order = 'desc') {
  try {
    const params = {};
    if (orderBy) {
      params.orderBy = orderBy;
      params.order = order;
    }
    
    const response = await apiClient.get('/api/user', { params });
    return response.data.data || [];
  } catch (error) {
    console.error("전체 사용자 목록 조회 오류:", error);
    return [];
  }
}

/**
 * 사용자 레벨 정보 조회
 * @param {string} userEmail - 사용자 이메일
 * @returns {Promise<Object>} { level: number, levelPoint: number }
 */
export async function getUserLevelInfo(userEmail) {
  try {
    const response = await apiClient.get(`/api/user/${encodeURIComponent(userEmail)}/level`);
    return response.data.data || { level: 1, levelPoint: 0 };
  } catch (error) {
    console.error("사용자 레벨 정보 조회 오류:", error);
    return { level: 1, levelPoint: 0 };
  }
}

/**
 * 사용자의 detailLink (트위터 링크 등) 조회
 * @param {string} userEmail - 사용자 이메일
 * @returns {Promise<string|null>} detailLink 또는 null
 */
export async function getUserDetailLink(userEmail) {
  try {
    const userData = await getUserInfo(userEmail);
    return userData?.detailLink || null;
  } catch (error) {
    console.error("사용자 detailLink 조회 오류:", error);
    return null;
  }
}

/**
 * 사용자 로그인 히스토리 조회
 * @param {string} userEmail - 사용자 이메일
 * @param {Object} options - 옵션 { limit, orderBy, order }
 * @returns {Promise<Array>} 로그인 히스토리 배열
 */
export async function getUserLoginHistory(userEmail, options = {}) {
  try {
    const { limit = 50, orderBy = 'timestamp', order = 'desc' } = options;
    const response = await apiClient.get(`/api/user/${encodeURIComponent(userEmail)}/login-history`, {
      params: { limit, orderBy, order }
    });
    return response.data.data || [];
  } catch (error) {
    console.error("로그인 히스토리 조회 오류:", error);
    return [];
  }
}

/**
 * 사용자 활동 로그 조회
 * @param {string} userEmail - 사용자 이메일
 * @param {Object} options - 옵션 { limit, orderBy, order }
 * @returns {Promise<Array>} 활동 로그 배열
 */
export async function getUserActivityLogs(userEmail, options = {}) {
  try {
    const { limit = 100, orderBy = 'timestamp', order = 'desc' } = options;
    const response = await apiClient.get(`/api/user/${encodeURIComponent(userEmail)}/activity-logs`, {
      params: { limit, orderBy, order }
    });
    return response.data.data || [];
  } catch (error) {
    console.error("활동 로그 조회 오류:", error);
    return [];
  }
}

/**
 * 전체 사용자 활동 집계
 * @param {Array<string>} excludeAdminEmails - 제외할 관리자 이메일 목록
 * @returns {Promise<Object>} 집계 데이터
 */
export async function getAggregatedActivity(excludeAdminEmails = []) {
  try {
    const params = {};
    if (excludeAdminEmails.length > 0) {
      params.excludeAdminEmails = excludeAdminEmails.join(',');
    }
    
    const response = await apiClient.get('/api/user/aggregate/activity', { params });
    return response.data.data || {
      aggregatedStats: {},
      userLastActivity: {},
      activeUserEmails: [],
    };
  } catch (error) {
    console.error("활동 집계 조회 오류:", error);
    return {
      aggregatedStats: {},
      userLastActivity: {},
      activeUserEmails: [],
    };
  }
}

/**
 * 익명 사용자 활동 로그 조회 (관리자용)
 * @param {number|object} daysOrOptions - 조회할 기간 (일) 또는 { startDate, endDate } 객체
 * @returns {Promise<Object>} { data: Array, stats: Object } 형태의 데이터
 */
export async function getAnonymousActivityStats(daysOrOptions = 30) {
  try {
    let params = {};
    
    if (typeof daysOrOptions === "object" && daysOrOptions.startDate && daysOrOptions.endDate) {
      params.startDate = daysOrOptions.startDate;
      params.endDate = daysOrOptions.endDate;
    } else {
      const days = typeof daysOrOptions === "number" ? daysOrOptions : 30;
      params.days = days;
    }
    
    const response = await apiClient.get('/api/user/anonymous-activity-stats', { params });
    return response.data.data || { data: [], stats: {} };
  } catch (error) {
    console.error("익명 활동 통계 조회 오류:", error);
    return { data: [], stats: {} };
  }
}

/**
 * 통합 활동 통계 조회 (로그인 + 비로그인, 사용자 식별 없이 통계만)
 * @param {number|object} daysOrOptions - 조회할 기간 (일) 또는 { startDate, endDate } 객체
 * @returns {Promise<Object>} 통계 데이터 (백엔드에서 올바른 구조로 반환)
 */
export async function getAllActivityStats(daysOrOptions = 30) {
  try {
    let params = {};
    
    if (typeof daysOrOptions === "object" && daysOrOptions.startDate && daysOrOptions.endDate) {
      params.startDate = daysOrOptions.startDate;
      params.endDate = daysOrOptions.endDate;
    } else {
      const days = typeof daysOrOptions === "number" ? daysOrOptions : 30;
      params.days = days;
    }
    
    const response = await apiClient.get('/api/user/all-activity-stats', { params });
    // 백엔드에서 올바른 구조로 반환 (loginUserCount, loginByType, anonymousByType 등 포함)
    return response.data.data || {
      total: 0,
      loginUserCount: 0,
      anonymousCount: 0,
      byType: {},
      byDevice: {},
      byBrowser: {},
      byTarget: {},
      timeline: {},
      loginByType: {},
      loginByTarget: {},
      loginTimeline: {},
      anonymousByType: {},
      anonymousByTarget: {},
      anonymousTimeline: {},
    };
  } catch (error) {
    console.error("통합 활동 통계 조회 오류:", error);
    return {
      total: 0,
      loginUserCount: 0,
      anonymousCount: 0,
      byType: {},
      byDevice: {},
      byBrowser: {},
      byTarget: {},
      timeline: {},
      loginByType: {},
      loginByTarget: {},
      loginTimeline: {},
      anonymousByType: {},
      anonymousByTarget: {},
      anonymousTimeline: {},
    };
  }
}

/**
 * 코어 유저 통계 조회 (관리자용)
 * @param {number} days - 조회할 기간 (일, 기본값: 14)
 * @param {number} minVisits - 최소 방문 횟수 (기본값: 3)
 * @returns {Promise<Array>} 코어 유저 목록
 */
export async function getCoreUserStats(days = 14, minVisits = 3) {
  try {
    const response = await apiClient.get('/api/user/core-users', {
      params: { days, minVisits }
    });
    return response.data.data || [];
  } catch (error) {
    console.error("코어 유저 통계 조회 오류:", error);
    return [];
  }
}

/**
 * 유입 경로 통계 조회 (관리자용)
 * @param {number|object} daysOrOptions - 조회할 기간 (일) 또는 { startDate, endDate } 객체
 * @returns {Promise<Object>} { data: Array, stats: Object } 형태의 데이터
 */
export async function getTrafficStats(daysOrOptions = 30) {
  try {
    let params = {};
    
    if (typeof daysOrOptions === "object" && daysOrOptions.startDate && daysOrOptions.endDate) {
      params.startDate = daysOrOptions.startDate;
      params.endDate = daysOrOptions.endDate;
    } else {
      const days = typeof daysOrOptions === "number" ? daysOrOptions : 30;
      params.days = days;
    }
    
    const response = await apiClient.get('/api/user/traffic-stats', { params });
    return response.data.data || { data: [], stats: {} };
  } catch (error) {
    console.error("유입 경로 통계 조회 오류:", error);
    return { data: [], stats: {} };
  }
}
